import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiMic, FiSearch, FiX } from 'react-icons/fi';
import {
  getVoicesDF,
  type DfVoice,
  type VoiceType,
  type VoiceCategory,
} from '../../../services/voiceDataFabricService';
import '../../../styles/Voice.css';

/* ── helpers ── */
function resolveGender(v: DfVoice): string | null {
  return v.labels?.gender ?? v.gender ?? null;
}

function genderColor(gender: string | null | undefined): string {
  if (!gender) return 'var(--text-muted)';
  const g = gender.toLowerCase();
  if (g === 'female') return '#f472b6';
  if (g === 'male') return '#60a5fa';
  return '#a78bfa';
}

const VOICE_TYPE_OPTIONS: { value: VoiceType | ''; label: string }[] = [
  { value: '', label: 'All voices' },
  { value: 'personal', label: 'My voices' },
  { value: 'community', label: 'Community' },
  { value: 'default', label: 'Default' },
  { value: 'workspace', label: 'Workspace' },
];

const CATEGORY_OPTIONS: { value: VoiceCategory | ''; label: string }[] = [
  { value: '', label: 'Any category' },
  { value: 'premade', label: 'Premade' },
  { value: 'cloned', label: 'Cloned' },
  { value: 'generated', label: 'Generated' },
  { value: 'professional', label: 'Professional' },
];

// gender/age aren't a documented ElevenLabs enum (they match free-text voice
// labels), but these three/three values are the overwhelmingly common
// convention across ElevenLabs' own voice library — offered as quick chips,
// not an exhaustive or authoritative list. Accent/use-case vary far more, so
// those stay free-text rather than guessing a fixed set.
const GENDER_CHIPS = ['male', 'female', 'neutral'];
const AGE_CHIPS = ['young', 'middle_aged', 'old'];

const PAGE_SIZE = 30;

/* ── VoiceCard ── */
const VoiceCard: React.FC<{ voice: DfVoice; selected: boolean; onSelect: () => void }> = ({
  voice, selected, onSelect,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const initial = (voice.name ?? '?')[0].toUpperCase();
  const gender = resolveGender(voice);
  const accent = voice.labels?.accent;
  const useCase = voice.labels?.use_case;

  const handlePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!voice.preview_url) return;
    if (playing) {
      audioRef.current?.pause();
      setPlaying(false);
      return;
    }
    if (!audioRef.current) {
      audioRef.current = new Audio(voice.preview_url);
      audioRef.current.onended = () => setPlaying(false);
    }
    audioRef.current.play();
    setPlaying(true);
  };

  return (
    <div
      className={`aep-voice-card${selected ? ' aep-voice-card--selected' : ''}`}
      onClick={onSelect}
      title={[accent, useCase].filter(Boolean).join(' · ') || undefined}
    >
      <div
        className="aep-voice-card__avatar"
        style={{ background: `${genderColor(gender)}22`, color: genderColor(gender) }}
      >
        {initial}
      </div>
      <div className="aep-voice-card__name">{voice.name ?? voice.voice_id}</div>
      {gender && (
        <span
          className="aep-voice-card__gender"
          style={{ color: genderColor(gender), borderColor: `${genderColor(gender)}44` }}
        >
          {gender.toUpperCase()}
        </span>
      )}
      <button
        className={`aep-voice-card__play${playing ? ' aep-voice-card__play--active' : ''}`}
        onClick={handlePlay}
        title={playing ? 'Stop' : 'Preview'}
      >
        {playing
          ? <span className="aep-voice-card__pause-icon" />
          : <span className="aep-voice-card__play-icon" />}
      </button>
      {selected && <span className="aep-voice-card__selected-badge">SELECTED</span>}
    </div>
  );
};

/* ── VoiceSelector ── */
interface Props {
  selectedVoiceId: string;
  onSelect: (voiceId: string) => void;
}

const VoiceSelector: React.FC<Props> = ({ selectedVoiceId, onSelect }) => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [voiceType, setVoiceType] = useState<VoiceType | ''>('');
  const [category, setCategory] = useState<VoiceCategory | ''>('');
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [accentInput, setAccentInput] = useState('');
  const [accent, setAccent] = useState('');
  const [useCaseInput, setUseCaseInput] = useState('');
  const [useCase, setUseCase] = useState('');

  const [voices, setVoices] = useState<DfVoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);

  const fetchSeq = useRef(0);

  /* Debounce free-text inputs before they trigger a search */
  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => window.clearTimeout(t);
  }, [searchInput]);
  useEffect(() => {
    const t = window.setTimeout(() => setAccent(accentInput.trim()), 400);
    return () => window.clearTimeout(t);
  }, [accentInput]);
  useEffect(() => {
    const t = window.setTimeout(() => setUseCase(useCaseInput.trim()), 400);
    return () => window.clearTimeout(t);
  }, [useCaseInput]);

  /* Any filter change resets to a fresh first page */
  useEffect(() => {
    const seq = ++fetchSeq.current;
    setLoading(true);
    setError(null);
    getVoicesDF({
      search: search || undefined,
      voiceType: voiceType || undefined,
      category: category || undefined,
      gender: gender || undefined,
      age: age || undefined,
      accent: accent || undefined,
      useCases: useCase ? [useCase] : undefined,
      sort: 'name',
      sortDirection: 'asc',
      pageSize: PAGE_SIZE,
    })
      .then((res) => {
        if (seq !== fetchSeq.current) return; // a newer filter change superseded this one
        setVoices(res.voices);
        setHasMore(res.has_more);
        setNextPageToken(res.next_page_token);
        setTotalCount(res.total_count);
      })
      .catch((e) => {
        if (seq !== fetchSeq.current) return;
        setError(e?.message ?? 'Failed to load voices');
      })
      .finally(() => {
        if (seq === fetchSeq.current) setLoading(false);
      });
  }, [search, voiceType, category, gender, age, accent, useCase]);

  const loadMore = useCallback(() => {
    if (!hasMore || !nextPageToken || loadingMore) return;
    setLoadingMore(true);
    const seq = fetchSeq.current;
    getVoicesDF({
      search: search || undefined,
      voiceType: voiceType || undefined,
      category: category || undefined,
      gender: gender || undefined,
      age: age || undefined,
      accent: accent || undefined,
      useCases: useCase ? [useCase] : undefined,
      sort: 'name',
      sortDirection: 'asc',
      pageSize: PAGE_SIZE,
      nextPageToken,
    })
      .then((res) => {
        if (seq !== fetchSeq.current) return;
        setVoices((prev) => [...prev, ...res.voices]);
        setHasMore(res.has_more);
        setNextPageToken(res.next_page_token);
      })
      .catch((e) => setError(e?.message ?? 'Failed to load more voices'))
      .finally(() => setLoadingMore(false));
  }, [hasMore, nextPageToken, loadingMore, search, voiceType, category, gender, age, accent, useCase]);

  const clearFilters = () => {
    setSearchInput(''); setAccentInput(''); setUseCaseInput('');
    setVoiceType(''); setCategory(''); setGender(''); setAge('');
  };
  const hasActiveFilters = !!(search || voiceType || category || gender || age || accent || useCase);

  return (
    <div className="vsel">
      <div className="vsel-filterbar">
        <div className="vsel-search">
          <FiSearch size={13} />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search voices by name or description…"
          />
          {searchInput && (
            <button className="vsel-search__clear" onClick={() => setSearchInput('')}>
              <FiX size={12} />
            </button>
          )}
        </div>

        <select className="vsel-select" value={voiceType} onChange={(e) => setVoiceType(e.target.value as VoiceType | '')}>
          {VOICE_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        <select className="vsel-select" value={category} onChange={(e) => setCategory(e.target.value as VoiceCategory | '')}>
          {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <div className="vsel-chip-row">
        <span className="vsel-chip-row__label">Gender</span>
        {GENDER_CHIPS.map((g) => (
          <button
            key={g}
            className={`vsel-chip${gender === g ? ' vsel-chip--active' : ''}`}
            onClick={() => setGender(gender === g ? '' : g)}
          >
            {g}
          </button>
        ))}
        <span className="vsel-chip-row__label">Age</span>
        {AGE_CHIPS.map((a) => (
          <button
            key={a}
            className={`vsel-chip${age === a ? ' vsel-chip--active' : ''}`}
            onClick={() => setAge(age === a ? '' : a)}
          >
            {a.replace('_', ' ')}
          </button>
        ))}
      </div>

      <div className="vsel-chip-row">
        <input
          className="vsel-text-filter"
          value={accentInput}
          onChange={(e) => setAccentInput(e.target.value)}
          placeholder="Accent (e.g. american, british…)"
        />
        <input
          className="vsel-text-filter"
          value={useCaseInput}
          onChange={(e) => setUseCaseInput(e.target.value)}
          placeholder="Use case (e.g. narration, conversational…)"
        />
        {hasActiveFilters && (
          <button className="vsel-clear-btn" onClick={clearFilters}>Clear filters</button>
        )}
      </div>

      {typeof totalCount === 'number' && !loading && (
        <div className="vsel-count">{totalCount.toLocaleString()} voice{totalCount === 1 ? '' : 's'} match{totalCount === 1 ? 'es' : ''}</div>
      )}

      {loading && (
        <div className="aep-voices-grid">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="aep-voice-card" style={{ pointerEvents: 'none' }}>
              <div className="skeleton skeleton-rect" style={{ width: 34, height: 34, borderRadius: 'var(--radius-md)', flexShrink: 0 }} />
              <div className="skeleton skeleton-text" style={{ flex: 1, height: 11 }} />
              <div className="skeleton skeleton-rect" style={{ width: 22, height: 22, borderRadius: 'var(--radius-sm)', flexShrink: 0 }} />
            </div>
          ))}
        </div>
      )}

      {error && !loading && (
        <div className="aep-voices-error">
          {error}
          <button className="aep-toolbox-btn" style={{ marginTop: 12 }} onClick={() => setSearch((s) => s)}>
            Retry
          </button>
        </div>
      )}

      {!loading && !error && voices.length === 0 && (
        <div className="aep-section aep-section--empty" style={{ padding: 0 }}>
          <div className="aep-empty-icon"><FiMic size={20} /></div>
          <div className="aep-empty-title">No voices found</div>
          <div className="aep-empty-desc">
            {hasActiveFilters ? 'Try loosening your filters.' : 'Voice library is empty or unavailable for this account.'}
          </div>
        </div>
      )}

      {!loading && voices.length > 0 && (
        <>
          <div className="aep-voices-grid">
            {voices.map((v) => (
              <VoiceCard
                key={v.voice_id}
                voice={v}
                selected={selectedVoiceId === v.voice_id}
                onSelect={() => onSelect(v.voice_id)}
              />
            ))}
          </div>
          {hasMore && (
            <button className="vsel-loadmore" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : 'Load more voices'}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default VoiceSelector;
